import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useCompanyId } from './useCompany';
import { audit } from '../../api/endpoints';
import { m } from 'motion/react';
import { ScrollText } from 'lucide-react';

import { Button } from '../../components/Button';
import { PageHeader } from '../../components/Surface';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';

/** `13 §2`: la traza de auditoría, filtrable y paginada por cursor. */
export function AuditScreen() {
  const t = useT();
  const format = useFormat();
  const companyId = useCompanyId();
  const [action, setAction] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);

  const params = new URLSearchParams({ limit: '25' });
  if (action) {
    params.set('filter[action]', action);
  }
  if (cursor) {
    params.set('cursor', cursor);
  }

  const query = useQuery({
    queryKey: ['audit', companyId, action, cursor],
    queryFn: () => audit.list(companyId, params),
    enabled: companyId !== '',
  });

  return (
    <div className="space-y-4">
      <PageHeader title={t('company.tabs.audit')} description={t('audit.intro')} />

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            // A filter change starts a new sequence: keeping the old cursor
            // would page through a result set it no longer belongs to.
            setCursor(null);
          }}
          placeholder={t('audit.filterPlaceholder')}
          className={cn(fieldStyles, 'grow border-line font-mono')}
        />
        {query.data ? (
          <span className="text-xs text-ink-faint">
            {t('audit.entries', { count: format.number(query.data.meta.total_estimate) })}
          </span>
        ) : null}
      </div>

      {query.isPending ? <SkeletonRows rows={4} /> : null}
      {query.error ? <ErrorBanner error={query.error} onRetry={() => void query.refetch()} /> : null}

      {query.data?.data.length === 0 ? (
        <EmptyState
          icon={<ScrollText />}
          tone="muted"
          title={t('audit.empty')}
          description={action ? t('audit.emptyFiltered') : t('audit.emptyHint')}
        />
      ) : null}

      {/*
        `action`, `actor_type` y `subject_type` son identificadores del contrato
        de auditoría y se pintan en crudo: la traza tiene que poder compararse
        carácter a carácter con lo que hay en la base de datos.
      */}
      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised">
        {query.data?.data.map((entry, index) => (
          <m.li
            key={entry.id}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: Math.min(index, 15) * 0.02, duration: 0.22 }}
            className="flex flex-wrap items-baseline gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-hover"
          >
            <time dateTime={entry.created_at} className="font-mono text-xs text-ink-faint">
              {format.dateTime(entry.created_at)}
            </time>
            <span className="font-mono font-semibold text-ink">{entry.action}</span>
            <span className="font-mono text-xs text-ink-faint">
              {entry.actor_type} → {entry.subject_type}
            </span>
          </m.li>
        ))}
      </ul>

      {query.data?.meta.has_more ? (
        <Button variant="secondary" onClick={() => setCursor(query.data.meta.next_cursor)}>
          {t('audit.loadOlder')}
        </Button>
      ) : null}
    </div>
  );
}
